import { Injectable } from '@angular/core';
import { add, addMilliseconds, isAfter } from 'date-fns/esm';
import { take } from 'rxjs/operators';
import { AuthService } from '~app/auth/auth.service';
import { AppStabilityService } from './app-stability.service';
import { AssertExtends } from './utils';

type CacheKey = string | number | Date | CacheKey[];
type _assertCacheKeyExtendsIDBValidKey = AssertExtends<CacheKey, IDBValidKey>;

interface StoreValue<T> {
  expires: Date;
  value: T;
}

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly STORE_NAME = 'cache';

  private db: Promise<IDBDatabase>;

  // Used to avoid the async nature of the store from causing multiple requests to the same resource at the same time
  private pendingResolves: Map<CacheKey, ((value: any) => void)[]> = new Map();

  constructor(
    stabilityService: AppStabilityService,
    private readonly authService: AuthService,
  ) {
    this.openDatabase();
    stabilityService.whenFirstStable()
      .then(() => this.db)
      .then(() => {
        // first cleanup after things have stabilized
        setTimeout(async () => {
          await this.cleanup();
        }, 1000 * 10);

        // further cleanups regularly
        setInterval(async () => {
          await this.cleanup();
        }, 1000 * 60 * 60);
      });
  }

  /**
   * "Connects" to the IndexedDB used for caching.
   * Sets up error logging and automatic reconnections.
   */
  private openDatabase(): void {
    // the IDB API uses `this` a lot so it's not available normally
    const self = this;

    this.db = new Promise(async (resolve, reject) => {
      // tenant ID might not yet be available when constructor is run
      await this.authService.login$.pipe(take(1)).toPromise();

      const req = indexedDB.open(`rego-cache-${this.authService.getTenant()}`, 1);
      req.onsuccess = function() {
        this.result.onerror = function(event) {
          console.error('CacheService DB error', this, event);
        };
        this.result.onabort = function(event) {
          console.error('CacheService DB abort', this, event);
        };
        this.result.onclose = function(event) {
          console.warn('CacheService DB closed', this, event);
          setTimeout(function() {
            self.openDatabase();
          }, 1000);
        };
        resolve(this.result);
      };
      req.onerror = function(event) {
        console.error('CacheService DB open error', this, event, this.error);
        reject(this.error);
      };
      req.onblocked = function(event) {
        console.error('CacheService DB open blocked', this, event);
        reject('blocked');
      };
      req.onupgradeneeded = function(event) {
        console.log('CacheService DB upgrade needed', this, event);
        const store = this.result.createObjectStore(self.STORE_NAME);
        store.createIndex('expires', 'expires', { unique: false });
      };
    });
  }

  /**
   * Start a transaction on the cache object store.
   */
  private async storeTransaction(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const trans = (await this.db).transaction(this.STORE_NAME, mode);
    return trans.objectStore(this.STORE_NAME);
  }

  /**
   * Removes expired entries
   */
  private async cleanup(): Promise<void> {
    const trans = await this.storeTransaction('readwrite');
    const req = trans.index('expires').openCursor(IDBKeyRange.upperBound(new Date()));
    if (req) {
      return new Promise((resolve, reject) => {
        req.onsuccess = function() {
          const cursor = this.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            // no more entries
            resolve();
          }
        };
        req.onerror = function(event) {
          console.error('CacheService cleanup error', this, event, this.error);
          reject(this.error);
        };
      });
    }
  }

  /**
   * Get a value by key.
   */
  private async rawGet<T>(key: IDBValidKey): Promise<StoreValue<T> | undefined> {
    const transaction = await this.storeTransaction('readonly');
    const request = transaction.get(key);
    return new Promise((resolve, reject) => {
      request.onsuccess = function() {
        resolve(this.result);
      };
      request.onerror = function(event) {
        console.error('CacheService rawGet error', this, event, this.error);
        reject(this.error);
      };
    });
  }

  /**
   * Write a value by key, overwriting any existing value.
   */
  private async rawSet<T>(key: IDBValidKey, value: StoreValue<T>): Promise<void> {
    const transaction = await this.storeTransaction('readwrite');
    const request = transaction.put(value, key);
    return new Promise((resolve, reject) => {
      request.onsuccess = function() {
        resolve();
      };
      request.onerror = function(event) {
        console.error('CacheService rawSet error', this, event, this.error);
        reject(this.error);
      };
    });
  }

  /**
   * Get a value by key.
   * If it doesn't exist, use `creator` to create a value and store it in the cache for `ttl` ms (or `Duration`).
   */
  public async getOrSet<K extends CacheKey, V>(key: K, ttl: number | Duration, creator: (key: K) => Promise<V>): Promise<V> {
    // non-stable `JSON.stringify` is safe, because `key` doesn't include objects
    const resolveKey = JSON.stringify(key); // Map compares using === so complex values don't work otherwise
    if (this.pendingResolves.has(resolveKey)) {
      return new Promise(resolve => {
        this.pendingResolves.get(resolveKey).push(resolve);
      });
    }
    this.pendingResolves.set(resolveKey, []);

    let value;
    const entry = await this.rawGet<V>(key);
    if (entry?.value && isAfter(entry.expires, new Date())) {
      value = entry.value;
    } else {
      value = await creator(key);
      await this.rawSet(key, {
        expires: typeof ttl === 'number' ? addMilliseconds(new Date(), ttl) : add(new Date(), ttl),
        value,
      });
    }

    for (const resolve of this.pendingResolves.get(resolveKey) || []) {
      resolve(value);
    }
    this.pendingResolves.delete(resolveKey);
    return value;
  }
}
